import React, { useState, FC, PropsWithChildren, useEffect, useMemo } from "react"
import { Outlet } from "react-router-dom"

import {
    FirestoreError,
    onSnapshot,
    collectionGroup,
    query,
    where,
    doc,
    Unsubscribe,
} from "firebase/firestore"

import deviceConverter from "./utils/deviceConverter"
import healthConverter from "../health-context/utils/healthConverter"
import { firebase } from "../../services/firebase/firebase"
import useContextMap from "../utils/useContextMap"
import { useAuth } from "../authentication-context"
import { DeviceDocument, DevicesContext } from "./index"
import useInferStatus from "../../features/devices/hooks/useInferStatus"
type DevicesProviderProps = {
    route?: boolean
}

export const DevicesProvider: FC<PropsWithChildren<DevicesProviderProps>> = ({ children, route = false }) => {
    const inferStatus = useInferStatus()
    // const { L } = useLanguage()
    const [data, setData] = useState<DeviceDocument[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<FirestoreError>()
    const active = useMemo(() => data.filter(device => device.health?.status === "on"), [data])
    const pending = useMemo(
        () =>
            data.filter(device => {
                const status = inferStatus(device.health)
                return status === "pending" || status === "uncertain"
            }),
        [data, inferStatus],
    )
    const map = useContextMap(data)
    const { user, isAuth } = useAuth()

    useEffect(() => {
        if (!user || !isAuth) return
        if (user.groups !== true && user.groups.length === 0) return

        const queryAllGroups = query(
            collectionGroup(firebase.firestore, "things"),
            where("organisation", "==", user.organisation),
        ).withConverter(deviceConverter)

        const devicesQuery =
            user.groups === true
                ? queryAllGroups
                : query(queryAllGroups, where("group", "in", user.groups))

        const healthUnsubs = new Map<string, Unsubscribe>()

        const unsubscribeDevices = onSnapshot(
            devicesQuery,
            devicesSnapshot => {
                const nextDevices = devicesSnapshot.docs.map(deviceDoc => deviceDoc.data())

                setData(prev => {
                    const previousHealth = new Map(
                        prev.map(device => [device.id, device.health ?? null]),
                    )

                    return nextDevices.map(device => ({
                        ...device,
                        health: previousHealth.get(device.id) ?? null,
                    }))
                })

                setLoading(false)

                const currentHealthPaths = new Set(
                    devicesSnapshot.docs.map(deviceDoc => `${deviceDoc.ref.path}/health/latest`),
                )

                for (const [healthPath, unsubscribe] of healthUnsubs) {
                    if (!currentHealthPaths.has(healthPath)) {
                        unsubscribe()
                        healthUnsubs.delete(healthPath)
                    }
                }

                devicesSnapshot.docs.forEach(deviceDoc => {
                    const device = deviceDoc.data()
                    const healthPath = `${deviceDoc.ref.path}/health/latest`

                    if (healthUnsubs.has(healthPath)) return

                    const healthRef = doc(firebase.firestore, healthPath).withConverter(healthConverter)

                    const unsubscribeHealth = onSnapshot(healthRef, healthSnapshot => {
                        const health = healthSnapshot.data() ?? null

                        setData(prev =>
                            prev.map(currentDevice =>
                                currentDevice.id === device.id
                                    ? {
                                        ...currentDevice,
                                        health,
                                    }
                                    : currentDevice,
                            ),
                        )
                    })

                    healthUnsubs.set(healthPath, unsubscribeHealth)
                })
            },
            setError,
        )

        return () => {
            unsubscribeDevices()

            for (const unsubscribe of healthUnsubs.values()) {
                unsubscribe()
            }

            healthUnsubs.clear()
        }
    }, [user, isAuth])

    // TODO: Re-consider usefulness of pending device status notification
    // useEffect(() => {
    //     if (pending.length)
    //         notifications.show({
    //             title:
    //                 pending.length === 1
    //                     ? L("%s has pending or unknown status", pending[0].name)
    //                     : pending.length === 2
    //                     ? L("%s and %s have pending or unknown status", pending[0].name, pending[1].name)
    //                     : L(
    //                           "%s, %s and %s other devices have pending or unknown status",
    //                           pending[0].name,
    //                           pending[1].name,
    //                           pending.length - 2,
    //                       ),
    //             message: (
    //                 <Text>
    //                     {L(
    //                         "We haven't heard from these devices in a while. This could be due to network issues or an empty battery.",
    //                     )}{" "}
    //                     <Anchor component={Link} to="devices">
    //                         {L("View devices")}
    //                     </Anchor>
    //                 </Text>
    //             ),
    //             color: "light",
    //             autoClose: 5_000,
    //             icon: <IconRouterOff size={20} />,
    //             withBorder: true,
    //         })
    // }, [pending])

    useEffect(() => {
        if (error) console.error(error)
    }, [error])

    return (
        <DevicesContext.Provider value={{ data, map, active, pending, loading, error }}>
            {route ? <Outlet /> : children}
        </DevicesContext.Provider>
    )
}
